// Ops · Riders: the rider profile drawer (docs/PRODUCT-GAPS-CONTRACT.md, contract.d.ts RiderProfile).
// GET /ops/riders/:userId/profile is Ops-only and includes the emergency contact. Pure: no DOM, no
// Intl, so node --test runs it.
import { initials } from '../../lib/format.js';
import { manilaStamp } from '../reports/period.js';

/** @typedef {import('@popcart/core/v1').RiderProfile} RiderProfile */
/** @typedef {import('@popcart/core/v1').VehicleType} VehicleType */

export const riderProfileKey = (/** @type {string} */ userId) => ['ops', 'rider-profile', userId];

/** @type {Record<VehicleType, string>} */
export const VEHICLE_LABEL = { motorcycle: 'Motorcycle', scooter: 'Scooter', bicycle: 'Bicycle', car: 'Car' };

/**
 * "+639171234567" → "0917 123 4567" (PH mobile); anything else is shown as sent.
 * @param {string | null | undefined} phone
 */
export function phoneDisplay(phone) {
  const p = String(phone ?? '').trim();
  if (!p) return null;
  const m = /^\+63(9\d{2})(\d{3})(\d{4})$/.exec(p.replace(/\s+/g, ''));
  return m ? `0${m[1]} ${m[2]} ${m[3]}` : p;
}

/**
 * A tap-to-call link: digits and a leading + only, or null.
 * @param {string | null | undefined} phone
 */
export function telHref(phone) {
  const p = String(phone ?? '').replace(/[^\d+]/g, '');
  return /^\+?\d{7,15}$/.test(p) ? `tel:${p}` : null;
}

/** @param {string | null | undefined} s */
const clean = (s) => (typeof s === 'string' && s.trim() ? s.trim() : null);

/**
 * Everything the drawer shows, already worded. Missing fields read "Not set" on screen (null here).
 * @param {RiderProfile} p
 * @param {{ name?: string | null }} [fallback] the riders list row, while the profile has no name
 */
export function profileView(p, fallback = {}) {
  const name = clean(p.name) ?? clean(fallback.name);
  const type = p.vehicle?.type ?? null;
  const ecName = clean(p.emergencyContact?.name);
  const ecPhone = clean(p.emergencyContact?.phone);
  return {
    userId: p.userId,
    title: name ?? 'No name yet',
    initials: initials(name, p.email),
    photoUrl: clean(p.photoUrl),
    phone: phoneDisplay(p.phone),
    phoneHref: telHref(p.phone),
    email: clean(p.email),
    vehicleType: type ? (VEHICLE_LABEL[type] ?? type) : null,
    vehicleDesc: clean(p.vehicle?.description),
    /** Bicycles carry no plate: say so instead of "Not set". */
    plate: clean(p.vehicle?.plate),
    plateNote: type === 'bicycle' && !clean(p.vehicle?.plate) ? 'No plate (bicycle)' : null,
    emergency: {
      name: ecName,
      phone: phoneDisplay(ecPhone),
      phoneHref: telHref(ecPhone),
      set: !!(ecName || ecPhone),
    },
    incomplete: p.complete !== true,
    updated: p.updatedAt ? `Updated ${manilaStamp(p.updatedAt)}` : 'Never updated',
  };
}

/** The rows of the facts list, in order (label, value or null = "Not set"). @param {ReturnType<typeof profileView>} v */
export function profileFacts(v) {
  return [
    { key: 'phone', label: 'Mobile', value: v.phone, href: v.phoneHref },
    { key: 'email', label: 'Email', value: v.email, href: null },
    { key: 'vehicle', label: 'Vehicle', value: v.vehicleType, href: null },
    { key: 'desc', label: 'Colour and model', value: v.vehicleDesc, href: null },
  ];
}
